import React, { useMemo, useState } from "react";
import {
    IonAccordion,
    IonButton,
    IonCol,
    IonGrid,
    IonIcon,
    IonItem,
    IonLabel,
    IonRow,
    IonSearchbar,
    IonSelect,
    IonSelectOption,
} from "@ionic/react";
import { pencilOutline, pencilSharp } from "ionicons/icons";
import { US_STATES_AND_TERRITORIES, US_REGION_NAMES } from "../../../constants/usRegions";
import type { Mapping, Provider, SettingsStateOption } from "../AcaSettingsTypes";

const US_STATES = US_STATES_AND_TERRITORIES;
const ALL_STATES: SettingsStateOption[] = Object.entries(US_REGION_NAMES).map(([code, name]) => ({code, name,}));

interface AcaSettingsProviderStatesSectionProps {
    data: {
        providers: Provider[];
        mappings: Mapping[];
    };
    actions: {
        openAddMappingModal: (stateCode: string) => void;
    };
}

const AcaSettingsProviderStatesSection: React.FC<AcaSettingsProviderStatesSectionProps> = ({data, actions,}) => {
    const { providers, mappings } = data;
    const { openAddMappingModal } = actions;

    const [stateQuery, setStateQuery] = useState("");
    const [stateProviderFilterIds, setStateProviderFilterIds] = useState<number[]>([]);
    const [stateMultiFilterStates, setStateMultiFilterStates] = useState<string[]>([]);

    const allProviders = useMemo(
        () =>
            (providers || [])
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name)),
        [providers]
    );

    const statesToShow = useMemo(() => {
        const query = (stateQuery || "").trim().toLowerCase();
        if (!query) {
            return US_STATES;
        }

        return US_STATES.filter((stateCode) => {
            const codeMatch = stateCode.toLowerCase().includes(query);
            const name = US_REGION_NAMES[stateCode as keyof typeof US_REGION_NAMES] || "";
            const nameMatch = name.toLowerCase().includes(query);
            return codeMatch || nameMatch;
        });
    }, [stateQuery]);

    const carriersByState = useMemo(() => {
        const byState: Record<string, { name: string; providerId: number }[]> = {};
        for (const stateCode of US_STATES) {
            byState[stateCode] = (mappings || [])
                .filter((mapping) => mapping.state === stateCode)
                .map((mapping) => ({ name: mapping.name, providerId: mapping.providerId }))
                .sort((a, b) => a.name.localeCompare(b.name));
        }
        return byState;
    }, [mappings]);

    const filteredStatesForTable = useMemo(() => {
        let baseStates = statesToShow;

        if (stateMultiFilterStates.length) {
            baseStates = baseStates.filter((stateCode) => stateMultiFilterStates.includes(stateCode));
        }

        if (!stateProviderFilterIds.length) {
            return baseStates;
        }

        return baseStates.filter((stateCode) => {
            const mapped = carriersByState[stateCode] || [];
            if (!mapped.length) {
                return false;
            }

            const mappedProviderIds = mapped.map((mapping) => mapping.providerId);
            return mappedProviderIds.some((id) => stateProviderFilterIds.includes(id));
        });
    }, [statesToShow, carriersByState, stateProviderFilterIds, stateMultiFilterStates]);

    return (
        <IonAccordion value="sec-states">
            <IonItem slot="header" color="light" lines="full">
                <IonLabel>Provider to State</IonLabel>
            </IonItem>
            <div slot="content">
                <section className="panel panel-default">
                    <IonGrid>
                        <IonRow className="ion-align-items-end">
                            <IonCol size="12" sizeMd="4">
                                <IonSearchbar
                                    value={stateQuery}
                                    placeholder="Filter by state code or name"
                                    onIonInput={(e) =>
                                        setStateQuery((e.detail.value ?? "").toString())
                                    }
                                    onIonClear={() => setStateQuery("")}
                                />
                            </IonCol>

                            <IonCol size="12" sizeMd="3">
                                <IonItem lines="none">
                                    <IonLabel position="stacked">Filter by provider</IonLabel>
                                    <IonSelect
                                        multiple
                                        interface="popover"
                                        value={stateProviderFilterIds}
                                        onIonChange={(e) =>
                                            setStateProviderFilterIds(
                                                ((e.detail.value as number[]) ?? []).map((value) => Number(value))
                                            )
                                        }
                                        placeholder="Any provider"
                                    >
                                        {allProviders.map((provider) => (
                                            <IonSelectOption key={provider.id} value={provider.id}>
                                                {provider.name}
                                            </IonSelectOption>
                                        ))}
                                    </IonSelect>
                                </IonItem>
                            </IonCol>
                            <IonCol size="12" sizeMd="3">
                                <IonItem lines="none">
                                    <IonLabel position="stacked">Filter by states</IonLabel>
                                    <IonSelect
                                        multiple
                                        interface="popover"
                                        value={stateMultiFilterStates}
                                        onIonChange={(e) =>
                                            setStateMultiFilterStates(
                                                ((e.detail.value as string[]) ?? []).map((value) => value.toString())
                                            )
                                        }
                                        placeholder="Any state"
                                    >
                                        {ALL_STATES.map((state) => (
                                            <IonSelectOption key={state.code} value={state.code}>
                                                {state.code} - {state.name}
                                            </IonSelectOption>
                                        ))}
                                    </IonSelect>
                                </IonItem>
                            </IonCol>
                            <IonCol size="12" sizeMd="2" className="btn-col ion-align-self-center">
                                <IonButton
                                    size="small"
                                    fill="outline"
                                    onClick={() => {
                                        setStateQuery("");
                                        setStateProviderFilterIds([]);
                                        setStateMultiFilterStates([]);
                                    }}
                                >
                                    Clear Filters
                                </IonButton>
                            </IonCol>
                        </IonRow>
                    </IonGrid>

                    <div
                        style={{
                            marginTop: 8,
                            overflow: "auto",
                            maxHeight: "400px",
                            display: "inline-block",
                            width: "100%",
                        }}
                    >
                        <table
                            className="aca-healthcare"
                            style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                borderSpacing: 0,
                                color: "#000",
                            }}
                        >
                            <thead>
                            <tr>
                                <th style={{ width: "16%", textAlign: "left" }}>State</th>
                                <th style={{ textAlign: "left" }}>Providers / Mappings</th>
                                <th style={{ width: "12%", textAlign: "center", whiteSpace: "nowrap" }}>
                                    Actions
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredStatesForTable.length === 0 && (
                                <tr>
                                    <td colSpan={3} style={{ textAlign: "center" }}>
                                        No states match your filters.
                                    </td>
                                </tr>
                            )}

                            {filteredStatesForTable.map((stateCode) => {
                                const mapped = carriersByState[stateCode] || [];

                                return (
                                    <tr key={stateCode}>
                                        <td>
                                            <strong>{stateCode}</strong>
                                            <br />
                                            <span
                                                style={{
                                                    fontSize: "0.85rem",
                                                    color: "var(--ion-color-medium)",
                                                }}
                                            >
                                                    {US_REGION_NAMES[stateCode as keyof typeof US_REGION_NAMES] || ""}
                                                </span>
                                        </td>

                                        <td>
                                            {mapped.length === 0 ? (
                                                <span style={{ color: "var(--ion-color-medium)" }}>
                                                        No providers mapped.
                                                    </span>
                                            ) : (
                                                <>
                                                    <span>{mapped.map((mapping) => mapping.name).join(", ")}</span>
                                                    <div
                                                        style={{
                                                            marginTop: 4,
                                                            fontSize: "0.8rem",
                                                            color: "var(--ion-color-medium)",
                                                        }}
                                                    >
                                                        {mapped.length} provider{mapped.length === 1 ? "" : "s"} mapped
                                                    </div>
                                                </>
                                            )}
                                        </td>

                                        <td style={{ textAlign: "center" }}>
                                            <IonButton
                                                size="small"
                                                fill="clear"
                                                className="xs-button"
                                                onClick={() => openAddMappingModal(stateCode)}
                                                title={`Manage providers for ${stateCode}`}
                                            >
                                                <IonIcon slot="icon-only" md={pencilOutline} ios={pencilSharp} />
                                            </IonButton>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </IonAccordion>
    );
};

export default AcaSettingsProviderStatesSection;